import { useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';
import type { ChannelSummary } from '@interfaces/ICommsApi';

const PARAM_CHANNEL = 'channel';

/**
 * Resolves the active channel from the `?channel=` URL param against the
 * caller-supplied list. The list is server-sourced (see `useChannels`), so
 * this hook is pure UI state + URL plumbing — no data fetch of its own.
 */
export function useOpenChannel(channels: ChannelSummary[]): {
  channel: ChannelSummary | null;
  /** The raw `?channel=` value, even when it doesn't resolve against `channels` —
   *  lets the caller synthesize a not-yet-persisted DM placeholder for it. */
  requestedId: string | null;
  open: (id: string) => void;
} {
  const [searchParams, setSearchParams] = useSearchParams();
  const requested = searchParams.get(PARAM_CHANNEL);

  // No default selection — first visit should land on the "pick a channel"
  // placeholder rather than auto-opening an arbitrary channel for the user.
  const channel = channels.find((c) => c.id === requested) ?? null;

  const open = useCallback(
    (id: string) => {
      setSearchParams(
        (prev) => {
          const params = new URLSearchParams(prev);
          params.set(PARAM_CHANNEL, id);
          return params;
        },
        { replace: true },
      );
    },
    [setSearchParams],
  );

  return { channel, requestedId: requested, open };
}
